import { ArrowLeft, ChevronDown, LogOut, User } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Language, useLanguageStore } from "~/stores/language-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import DE from 'country-flag-icons/react/3x2/DE';
import GB from 'country-flag-icons/react/3x2/GB';

type TopProgressBarProps = {
    percentageFilled: number;
    displayString: string;
    onClickBackButton?: () => void;
    color?: "primary" | "green";
}

const TopProgressBar = ({ percentageFilled, displayString, onClickBackButton, color = "primary" }: TopProgressBarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language, setLanguage, t } = useLanguageStore();
    const { customer, isAuthenticated, logout, startLogin } = useCustomerAuthStore();

    const handleLogout = () => {
        logout();
    };

    const handleLogin = () => {
        startLogin(location.pathname + location.search);
        navigate("/login");
    };

    const isLoggedIn = isAuthenticated() && customer;

    return (
        <div className='w-full flex flex-row pt-6 pb-2 px-4 justify-between items-center sticky top-0 z-50 bg-white rounded-b-lg overflow-hidden'>
            {/* Left: Back button */}
            <div className='w-20'>
                {onClickBackButton ? (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClickBackButton}
                        className='w-10 h-10'
                    >
                        <ArrowLeft className='w-5! h-5! text-dark-gray' />
                    </Button>
                ) : null}
            </div>

            {/* Center: Progress text - absolutely positioned for perfect centering */}
            <span className='absolute left-1/2 -translate-x-1/2 text-dark-gray tracking-wide text-sm font-light'>
                {displayString}
            </span>

            {/* Right: Language + Login/Profile side by side */}
            <div className='flex items-center justify-end gap-1'>
                {/* Language selector dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className='h-10 px-2 gap-0.5 hover:bg-light-burgundy'
                            aria-label={language === Language.GERMAN ? 'Sprache wählen' : 'Select language'}
                        >
                            {language === Language.GERMAN
                                ? <DE className='w-5 h-4' />
                                : <GB className='w-5 h-4' />
                            }
                            <ChevronDown className='w-3 h-3 text-burgundy/50' />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={() => setLanguage(Language.GERMAN)}
                            className='flex items-center gap-2 cursor-pointer'
                        >
                            <DE className='w-5 h-4' />
                            <span>Deutsch</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => setLanguage(Language.ENGLISH)}
                            className='flex items-center gap-2 cursor-pointer'
                        >
                            <GB className='w-5 h-4' />
                            <span>English</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Login/Profile button */}
                {isLoggedIn ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className='w-10 h-10 text-burgundy hover:bg-light-burgundy'
                                aria-label={t('logout')}
                            >
                                <User className='w-5! h-5! fill-burgundy' />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleLogout} className='flex items-center gap-2 cursor-pointer text-burgundy'>
                                <LogOut className='w-4 h-4' />
                                <span>{t('logout')}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : (
                    <Button
                        variant="ghost"
                        onClick={handleLogin}
                        className='h-10 px-2 flex flex-col items-center justify-center gap-0 text-burgundy hover:bg-light-burgundy'
                        aria-label={t('loginPromptLink')}
                    >
                        <User className='w-4! h-4!' />
                        <span className='text-[9px] font-medium leading-tight'>Login</span>
                    </Button>
                )}
            </div>

            {/* Progress bar */}
            <div className='absolute h-[2px] -bottom-px left-0 right-0 bg-light-gray rounded-full'>
                <div
                    className={`h-full rounded-full transition-[width] duration-500 ease-in-out ${color === "green" ? "bg-green-600" : "bg-primary-400"}`}
                    style={{
                        width: `${percentageFilled}%`,
                    }}
                />
            </div>
        </div>
    )
}

export default TopProgressBar;
